import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ItineraryHistoryEntry, TripItineraryResult } from '@/utils/tripPlannerTypes';

export const MAX_TRIP_DAYS = 10;

export type TripPlannerSection = 'what' | 'when' | 'details' | 'notes';
export type TripPlannerStatus = 'idle' | 'loading' | 'success' | 'error';
export type TripPlannerDateMode = 'month' | 'dates';
export type TripPlannerState = {
  what: string;
  month: string;
  dateMode: TripPlannerDateMode;
  numberOfDays: number;
  // Stored as ISO strings so the slice stays serializable; the sidebar converts to Date objects for the calendar.
  startDate?: string;
  endDate?: string;
  notes: string;
  difficultyIds: string[];
  featureIds: string[];
  customFeatures: string[];
  openSection: TripPlannerSection | null;
  status: TripPlannerStatus;
  // Incremented on every generation so an abandoned run can tell that a newer one has taken over.
  requestId: number;
  result?: TripItineraryResult;
  taskId?: string;
  errorMessage?: string;
  isRateLimited?: boolean;
  // Only set when the usage-limit response carried a parseable Retry-After.
  rateLimitResetInDays?: number;
  expandedDayIndex: number | null;
  loadingFromHistory: boolean;
  showHistory: boolean;
  historyStatus: TripPlannerStatus;
  history: ItineraryHistoryEntry[];
  historyRequestId: number;
  disclosureOpen: boolean;
};

export const initialState: TripPlannerState = {
  what: '',
  month: '',
  dateMode: 'month',
  numberOfDays: 0,
  notes: '',
  difficultyIds: [],
  featureIds: [],
  customFeatures: [],
  openSection: 'what',
  status: 'idle',
  requestId: 0,
  expandedDayIndex: null,
  loadingFromHistory: false,
  showHistory: false,
  historyStatus: 'idle',
  history: [],
  historyRequestId: 0,
  disclosureOpen: false
};

const clearError = (state: TripPlannerState) => {
  if (state.status === 'error') {
    state.status = 'idle';
    state.errorMessage = undefined;
    state.isRateLimited = undefined;
    state.rateLimitResetInDays = undefined;
  }
};

export const tripPlannerSlice = createSlice({
  name: 'tripPlanner',
  initialState,
  reducers: {
    resetState: (state, action: PayloadAction<{ persistKeys?: (keyof TripPlannerState)[]; initialState?: Partial<TripPlannerState> }>) => {
      const defaultState: TripPlannerState = { ...initialState, ...(action.payload.initialState || {}) };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (defaultState[key] as typeof persistValue) = persistValue;
      });

      return defaultState;
    },
    setTripPlannerWhat: (state, action: PayloadAction<string>) => {
      state.what = action.payload;
      clearError(state);
    },
    setTripPlannerMonth: (state, action: PayloadAction<string>) => {
      state.month = action.payload;
      clearError(state);
    },
    setTripPlannerDateMode: (state, action: PayloadAction<TripPlannerDateMode>) => {
      state.dateMode = action.payload;
      // The two modes are mutually exclusive ways to answer "when"; drop the inactive mode's fields so the stored
      // state, the When summary, and submission never disagree over stale values the user can no longer see.
      if (action.payload === 'dates') {
        state.month = '';
        state.numberOfDays = 0;
      } else {
        state.startDate = undefined;
        state.endDate = undefined;
      }
      clearError(state);
    },
    setTripPlannerNumberOfDays: (state, action: PayloadAction<number>) => {
      state.numberOfDays = Math.min(MAX_TRIP_DAYS, Math.max(0, action.payload));
      clearError(state);
    },
    setTripPlannerDateRange: (state, action: PayloadAction<{ startDate?: string; endDate?: string }>) => {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
      clearError(state);
    },
    setTripPlannerNotes: (state, action: PayloadAction<string>) => {
      state.notes = action.payload;
      clearError(state);
    },
    toggleTripPlannerDifficulty: (state, action: PayloadAction<string>) => {
      const index = state.difficultyIds.indexOf(action.payload);
      if (index >= 0) {
        state.difficultyIds.splice(index, 1);
      } else {
        state.difficultyIds.push(action.payload);
      }
      clearError(state);
    },
    toggleTripPlannerFeature: (state, action: PayloadAction<string>) => {
      const index = state.featureIds.indexOf(action.payload);
      if (index >= 0) {
        state.featureIds.splice(index, 1);
      } else {
        state.featureIds.push(action.payload);
      }
      clearError(state);
    },
    addTripPlannerCustomFeature: (state, action: PayloadAction<string>) => {
      const label = action.payload.trim();
      if (!label) {
        return;
      }
      if (!state.customFeatures.includes(label)) {
        state.customFeatures.push(label);
      }
      if (!state.featureIds.includes(label)) {
        state.featureIds.push(label);
      }
      clearError(state);
    },
    updateTripPlannerCustomFeature: (state, action: PayloadAction<{ previousLabel: string; label: string }>) => {
      const { previousLabel } = action.payload;
      const label = action.payload.label.trim();
      const index = state.customFeatures.indexOf(previousLabel);
      if (index < 0 || !label) {
        return;
      }
      state.customFeatures[index] = label;
      state.featureIds = state.featureIds.map(id => (id === previousLabel ? label : id));
      clearError(state);
    },
    removeTripPlannerCustomFeature: (state, action: PayloadAction<string>) => {
      state.customFeatures = state.customFeatures.filter(label => label !== action.payload);
      state.featureIds = state.featureIds.filter(id => id !== action.payload);
      clearError(state);
    },
    clearTripPlannerForm: state => {
      state.what = '';
      state.month = '';
      state.dateMode = initialState.dateMode;
      state.numberOfDays = 0;
      state.startDate = undefined;
      state.endDate = undefined;
      state.notes = '';
      state.difficultyIds = [];
      state.featureIds = [];
      state.customFeatures = [];
      state.openSection = initialState.openSection;
      clearError(state);
    },
    setTripPlannerOpenSection: (state, action: PayloadAction<TripPlannerSection | null>) => {
      state.openSection = action.payload;
    },
    tripPlannerRequestStarted: (state, action: PayloadAction<{ fromHistory?: boolean; taskId?: string } | undefined>) => {
      state.status = 'loading';
      state.requestId += 1;
      state.result = undefined;
      state.errorMessage = undefined;
      state.isRateLimited = undefined;
      state.rateLimitResetInDays = undefined;
      state.showHistory = false;
      state.loadingFromHistory = action.payload?.fromHistory ?? false;
      state.taskId = action.payload?.taskId;
    },
    tripPlannerRequestSucceeded: (state, action: PayloadAction<TripItineraryResult>) => {
      state.status = 'success';
      state.result = action.payload;
      state.errorMessage = undefined;
      state.isRateLimited = undefined;
      state.rateLimitResetInDays = undefined;
      state.expandedDayIndex = null;
      state.disclosureOpen = true;
    },
    tripPlannerRequestFailed: (
      state,
      action: PayloadAction<{ message?: string; isRateLimited?: boolean; rateLimitResetInDays?: number } | undefined>
    ) => {
      state.status = 'error';
      state.errorMessage = action.payload?.message;
      state.isRateLimited = action.payload?.isRateLimited;
      state.rateLimitResetInDays = action.payload?.rateLimitResetInDays;
    },
    resetTripPlannerItinerary: state => {
      state.status = 'idle';
      state.result = undefined;
      state.errorMessage = undefined;
      state.isRateLimited = undefined;
      state.rateLimitResetInDays = undefined;
      state.expandedDayIndex = null;
      state.taskId = undefined;
      state.disclosureOpen = false;
    },
    setTripPlannerDisclosureOpen: (state, action: PayloadAction<boolean>) => {
      state.disclosureOpen = action.payload;
    },
    setTripPlannerExpandedDay: (state, action: PayloadAction<number | null>) => {
      state.expandedDayIndex = action.payload;
    },
    setTripPlannerTaskId: (state, action: PayloadAction<string | undefined>) => {
      state.taskId = action.payload;
    },
    setShowHistory: (state, action: PayloadAction<boolean>) => {
      state.showHistory = action.payload;
    },
    tripPlannerHistoryStarted: state => {
      state.historyStatus = 'loading';
      state.historyRequestId += 1;
    },
    tripPlannerHistorySucceeded: (state, action: PayloadAction<ItineraryHistoryEntry[]>) => {
      state.historyStatus = 'success';
      state.history = action.payload;
    },
    tripPlannerHistoryFailed: state => {
      state.historyStatus = 'error';
    }
  }
});

export const {
  resetState,
  setTripPlannerWhat,
  setTripPlannerMonth,
  setTripPlannerDateMode,
  setTripPlannerNumberOfDays,
  setTripPlannerDateRange,
  setTripPlannerNotes,
  toggleTripPlannerDifficulty,
  toggleTripPlannerFeature,
  addTripPlannerCustomFeature,
  updateTripPlannerCustomFeature,
  removeTripPlannerCustomFeature,
  clearTripPlannerForm,
  setTripPlannerOpenSection,
  tripPlannerRequestStarted,
  tripPlannerRequestSucceeded,
  tripPlannerRequestFailed,
  resetTripPlannerItinerary,
  setTripPlannerDisclosureOpen,
  setTripPlannerExpandedDay,
  setTripPlannerTaskId,
  setShowHistory,
  tripPlannerHistoryStarted,
  tripPlannerHistorySucceeded,
  tripPlannerHistoryFailed
} = tripPlannerSlice.actions;
export default tripPlannerSlice.reducer;
